import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { validateLifeInsuranceSuggestion } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewColumnData } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewRow } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewRow';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { formatCurrency } from '@/utils/formatting/currency';

interface AISuggestionReviewLifeInsuranceRowProps {
  suggestion: AiSuggestionForReviewCard;
}

export function AISuggestionReviewLifeInsuranceRow({
  suggestion,
}: AISuggestionReviewLifeInsuranceRowProps) {
  const { onReviewClick, onDeleteClick, onCreateClick } =
    useAISuggestionReviewCardContext();
  const { possibleGrantors } = useHouseholdDetailsContext();

  const { columnsData, hasWarning } = useMemo<{
    columnsData: AISuggestionReviewColumnData[];
    hasWarning: boolean;
  }>(() => {
    const s = suggestion.lifeInsurance;
    if (!s) return { columnsData: [], hasWarning: false };

    const {
      policyTypeMissing,
      deathBenefitMissing,
      insuredMissing,
      hasWarning,
    } = validateLifeInsuranceSuggestion(suggestion, possibleGrantors || []);

    const policyTypeText = s.policyType
      ? InsurancePolicyTypeCopyMap[s.policyType]
      : '';

    // Format the death benefit if it exists
    const deathBenefitText = s.deathBenefitAmount
      ? formatCurrency(new Decimal(s.deathBenefitAmount))
      : '';

    const insuredClientProfiles = s.insuredClientProfiles || [];
    const insuredNames = insuredClientProfiles
      .map((profile) => {
        const client = possibleGrantors?.find(
          (g) => g.id === profile.clientProfileID
        );
        return client?.displayName;
      })
      .filter(Boolean);
    const insuredText = insuredNames.join(', ');

    const warnPolicyTypeTooltip = policyTypeMissing
      ? 'Missing policy type. Review required.'
      : undefined;
    const warnDeathBenefitTooltip = deathBenefitMissing
      ? 'Missing death benefit amount. Review required.'
      : undefined;
    const warnInsuredTooltip = insuredMissing
      ? 'Missing insured people. Review required.'
      : undefined;

    const columns: AISuggestionReviewColumnData[] = [
      {
        label: 'Policy type',
        values: [
          {
            displayText: policyTypeText || '-',
            showWarning: policyTypeMissing,
            warningTooltip: warnPolicyTypeTooltip,
          },
        ],
      },
      {
        label: 'Insured',
        values: [
          {
            displayText: insuredText || '-',
            showWarning: insuredMissing,
            warningTooltip: warnInsuredTooltip,
          },
        ],
      },
      {
        label: 'Death benefit',
        values: [
          {
            displayText: deathBenefitText || '-',
            showWarning: deathBenefitMissing,
            warningTooltip: warnDeathBenefitTooltip,
          },
        ],
      },
    ];

    return { columnsData: columns, hasWarning };
  }, [possibleGrantors, suggestion]);

  if (!suggestion.lifeInsurance) return null;

  return (
    <AISuggestionReviewRow
      suggestionID={suggestion.id}
      rowHasWarning={hasWarning}
      columnsData={columnsData}
      onCreateClick={onCreateClick}
      onReviewClick={onReviewClick}
      onDeleteClick={onDeleteClick}
      columnCount={3}
    />
  );
}
